<template>
  <div v-if="isLoading" class="isLoadingDiv">
    <div v-if="isDebug">{{ $t("loading-isloading", [isLoading]) }}</div>
    <ProgressSpinner />
  </div>
  <div v-else-if="venue" class="p-grid p-text-left mt-5">
    <UpgradeBanner v-if="currentUser && !currentUser.upgraded" />
    <div class="p-col-12 p-my-3">
      <h1 class="title is-1 displayInline">{{ venue.name }}</h1>
      <Button
        class="p-button-lg p-button-outlined floatRight"
        :label="$t('Button.edit')"
        @click="goRouter(`/venues/${venue.id}/edit`)"
      />
    </div>
    <div class="p-col-4 rounded paddedCol whiteBg">
      <div>
        <p class="title is-5">{{ $t("address") }}</p>
        <a v-if="venue.address.link" :href="venue.address.link" target="blank">
          {{ venue.address.formatted }}
        </a>
        <div v-if="venue.address.line_1">
          {{ venue.address.formatted }}
        </div>
        <div v-else>
          {{ $t("no-address-listed") }} <a @click="goRouter(`/venues/${venue.id}/edit`)"> {{ $t("add-address") }} </a>
        </div>
      </div>
      <div class="p-mt-2">
        <p class="title is-5">{{ $t("Wizard.manual_venue_form.phone") }}</p>
        <a v-if="venue.address.phone_primary" :href="'tel:' + venue.address.phone_primary" target="blank">
          {{ venue.address.phone_primary }}
        </a>
        <div v-else>{{ $t("edit-venue-to-add-phone") }}</div>
      </div>
      <div class="p-mt-3">
        <p class="title is-5">{{ $t("playing-surfaces") }}</p>
        <div>
          {{ venue.surfaces.length }}
        </div>
      </div>
      <div class="p-mt-3">
        <p class="title is-5">{{ $t("venue-priority") }}</p>
        <div>{{ venue.priority }}</div>
      </div>
      <div class="p-mt-2">
        <p class="title is-5">{{ $t("home-teams") }}</p>
        <div>
          <span
            v-if="venue.force_neutral"
            v-tooltip="'Any team can use this for home games. Click edit to change this.'"
          >
            {{ $t("forced-neutral-venue") }}
          </span>
          <span v-else-if="venue.is_neutral">
            {{ $t("neutral-venue-no-home-teams") }}
          </span>
          <span v-for="(d, i) in venue.home_teams" :key="d.id">
            <router-link :to="`/teams/${d.team_id}`" v-html="d.team.name"> </router-link>
            <span v-if="i != venue.home_teams.length - 1">, </span>
          </span>
          <div class="mt-2">
            <p class="button is-small" @click="$modal.show('hometeams')">
              <span v-if="venue.home_teams.length == 0"> {{ $t("add-home-teams") }} </span>
              <span v-else> {{ $t("Button.edit") }} </span>
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="p-col-8 p-pl-5 nopadding" id="map_container">
      <div class="whiteBg rounded" style="padding: 10px">
        <div id="map"></div>
      </div>
    </div>
    <div class="p-col-12 rounded paddedCol whiteBg p-mt-5">
      <div class="p-grid">
        <div class="p-col-6">
          <p class="title is-5">{{ $t("notes") }}</p>
          <div>{{ venue.notes || $t('no-notes-added-edit-venue-to-add') }}</div>
        </div>
        <div class="p-col-6" v-if="currentSeason && currentSeason.venues.length > 1">
          <p class="title is-5">{{ $t("distances") }}</p>
          <p v-if="venue.distances.length == 0">
            {{ $t("calculating-please-check-back-soon") }}
          </p>
          <p v-for="d in venue.distances" :key="d.id">
            <router-link v-if="d.from_id != venue.id" :to="`/venues/${d.from_id}`">
              {{ d.from.name }}
            </router-link>
            <router-link v-if="d.to_id != venue.id" :to="`/venues/${d.to_id}`">
              {{ d.to.name }}
            </router-link>
            <span> {{ $t("d-miles-or-or-0-tofixed-1-miles", [(d.miles || 0).toFixed(1)]) }} </span>
            <span> {{ $t("d-kilometers-or-or-0-tofixed-1-kilometers", [(d.kilometers || 0).toFixed(1)]) }} </span>
          </p>
        </div>
      </div>
    </div>

    <div v-for="surface in venue.surfaces" :key="surface.id" class="p-col-12">
      <div class="p-grid rounded paddedCol whiteBg p-mt-2">
        <div class="p-col-3">
          <strong> {{ $t("Division.name") }} </strong>
          <div>{{ surface.name }}</div>
        </div>
        <!--
        <div class="p-col-3">
          <strong> Priority </strong>
          <div>{{ surface.priority || "N/A" }}</div>
        </div> -->
        <div class="p-col-12">
          <strong> {{ $t("notes") }} </strong>
          <div>{{ surface.notes || $t("none") }}</div>
        </div>
      </div>
    </div>
    <modal name="hometeams" :adaptive="true" :scrollable="true" :reset="true" height="auto">
      <div class="modal-wrapper">
        <div class="modal-header">
          <div class="title is-3 p-mb-1">{{ $t("edit-home-teams") }}</div>
          <p class="">{{ $t("click-a-team-to-add-or-remove-from-list") }}</p>
        </div>
        <div class="modal-content">
          <div class="columns">
            <div class="column">
              <p class="bold">{{ $t("eligible-teams") }} <br/></p>
              <div v-if="eligibleTeams.length > 0">
                <div v-for="team in eligibleTeams" :key="team.id">
                  <a @click="createVenueTeam(team)">
                    {{ team.name }} ({{ team.division_abbreviation || team.division_name }})
                  </a>
                </div>
              </div>
            </div>
            <div class="column">
              <p class="bold">{{ $t("home-teams") }} <br /></p>
              <div v-for="venue_team in venue.home_teams" :key="venue_team.id">
                <a @click="removeVenueTeam(venue_team)">
                  {{ venue_team.team.name }}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <a class="button" @click="$modal.hide('hometeams')"> {{ $t("Button.done") }} </a>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import api from "@/api";
export default {
  components: {},
  data() {
    return {
      venue: {},
      isLoading: true,
      map: null,
    };
  },
  computed: {
    venueTeamIDs() {
      var team_ids = [];
      this.venue.home_teams.forEach(c => {
        team_ids.push(c.team.id);
      });
      return team_ids;
    },
    eligibleTeams() {
      return this.$store.getters.currentSeason.teams.filter(el => {
        return this.venueTeamIDs.indexOf(el.id) < 0;
      });
    },
  },
  methods: {
    getData: function() {
      console.log("GETTING", this.$route.params.venue_id, this.$store.getters.currentSeason.id);
      api
        .getVenue(this.$route.params.venue_id, this.$store.getters.currentSeason.id)
        .then(response => {
          console.log(response.data);
          this.venue = response.data;
          this.isLoading = false;
          console.log("isLoading is now " + this.isLoading);
          console.log(this.venue.address);
          if (this.venue.address && this.venue.address.latitude) {
            setTimeout(() => {
              console.log("Creating map");
              var x = document.getElementById("map");
              if (!x) {
                console.log("No container");
                return;
              }
              if (this.map) {
                this.map.remove();
              }
              this.map = L.map("map").setView([this.venue.address.latitude, this.venue.address.longitude], 15);
              var marker = L.marker([this.venue.address.latitude, this.venue.address.longitude]).addTo(this.map);
              var popup = `<b>${this.venue.name}</b><br>${this.venue.address.formatted}<br>${this.venue.notes || ""}`;
              marker.bindPopup(popup).openPopup();
              L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
              }).addTo(this.map);
            }, 1000);
          }
        })
        .catch(e => {
          this.makeToast("error", this.$t("could-not-load-venue"));
          this.goRouter("/");
        });
    },
    createVenueTeam(team) {
      console.log("Create venue team: " + team.name);
      api
        .createVenueTeam(team.id, this.venue.id)
        .then(response => {
          this.getData();
          this.makeToast("success", this.$t("added-team-name-to-home-teams", [team.name]));
        })
        .catch(error => {
          this.errors = error.response.data.errors;
        });
    },
    removeVenueTeam(venue_team) {
      console.log("Remove venue team: " + venue_team.id);
      api
        .removeVenueTeam(venue_team)
        .then(response => {
          this.getData();
          this.makeToast("info", this.$t("remove-venue_team-team-name-from-home-teams", [venue_team.team.name]));
        })
        .catch(error => {
          this.errors = error.response.data.errors;
        });
    },
  },
  mounted() {
    this.getData();
    window.analytics.page();
  },
  watch: {
    "$route.params.venue_id": function(id) {
      console.log("route changed, loading true");
      this.isLoading = true;
      this.getData();
      window.analytics.page();
    },
  },
};
</script>

<style scoped lang="scss">
#map {
  width: 100%;
  // min-width: 200px;
  height: 100%;
  min-height: 300px;
  // min-height: 200px;
}

.surface_wrapper {
  border: 1px solid grey;
}
</style>
